import useReservationGroupUpsertGridColumn from './reservation-group-upsert-grid-columns'
import useReservationGroupUpsertRows from './reservation-group-upsert-rows'
import { Typography } from '@mui/material'
import { Checkbox, FormControlLabel, Stack } from '@vapor/material'
import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { HicDataGridPremium } from 'src/app/components/hic-vapor/datagrid/datagrid'
import { ReservationGroupUpsertGridRow, ReservationRatesPerOccupancy, ResourceTypePerOccupancy, isReservationGroupUpsertGridTotalRow } from '../reservation-group.types'

type ReservationGroupUpsertGridProps = {
  start: Date
  end: Date
  nights: number
  propertyId: string
  reservationRates: ReservationRatesPerOccupancy
  onCartChanges: (rows: ReservationGroupUpsertGridRow[]) => void
  rateId?: string
  chargeCategoryId?: string
  resourceTypePerOccupancy: ResourceTypePerOccupancy
}

const ReservationGroupUpsertHideChildrenFilter = 'reservation-group-upsert-hide-children-filter'

export default function ReservationGroupUpsertGrid(props: ReservationGroupUpsertGridProps) {
  const { start, end, nights, reservationRates, propertyId, onCartChanges, resourceTypePerOccupancy } = props
  const { t } = useTranslation()
  const [hideChildren, setHideChildren] = useState(
    (() => {
      const lsValue = localStorage.getItem(ReservationGroupUpsertHideChildrenFilter)
      if (lsValue) return lsValue === 'true'
      return true
    })(),
  )

  const childrenAgeRange = useMemo(() => {
    if (!reservationRates) return null
    const childrenAgeRange = Object.values(reservationRates)[0]?.rateQuery?.rates?.[0]?.rate?.childRule
    return childrenAgeRange?.childDiscount?.filter((cd) => cd.child.auditable.state === 'ACTIVE') ?? null
  }, [reservationRates])

  const { dataGridRows, updateRowResourcesNumber, updateRowOccupancy, updateRowTotalPrice, createRow, removeRow, updateTotalRowTotalPrice, updateRowInitialPrice } = useReservationGroupUpsertRows(
    start,
    end,
    nights,
    propertyId,
    hideChildren,
    childrenAgeRange,
    reservationRates,
    resourceTypePerOccupancy,
  )
  const { columns } = useReservationGroupUpsertGridColumn(
    hideChildren,
    childrenAgeRange?.map((c) => c.child.taxonomy.description) ?? [],
    reservationRates,
    updateRowResourcesNumber,
    updateRowOccupancy,
    updateRowTotalPrice,
    updateRowInitialPrice,
    createRow,
    removeRow,
    updateTotalRowTotalPrice,
  )

  useEffect(() => {
    localStorage.setItem(ReservationGroupUpsertHideChildrenFilter, String(hideChildren))
  }, [hideChildren])

  useEffect(() => {
    onCartChanges((dataGridRows.filter((row) => !isReservationGroupUpsertGridTotalRow(row) && row.resourceNumber > 0) ?? []) as ReservationGroupUpsertGridRow[])
  }, [dataGridRows])

  return (
    <>
      {props.rateId && props.chargeCategoryId && (
        <Stack direction="column" spacing={1}>
          {(childrenAgeRange ?? []).length > 0 && (
            <Stack maxWidth="164px">
              <FormControlLabel
                control={<Checkbox size="small" checked={!hideChildren} onChange={(value) => setHideChildren(!value.target.checked)} />}
                label={<Typography variant="body">{t('pms.booking.reservation_group_new.grid.show_children_column')}</Typography>}
                labelPlacement="end"
              />
              <div style={{ flexGrow: 2 }} />
            </Stack>
          )}
          <HicDataGridPremium
            columns={columns}
            rows={dataGridRows}
            autoHeight
            rowHeight={80}
            disableRowSelectionOnClick={true}
            sx={{
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'break-spaces',
                lineHeight: 1,
              },
              '&.MuiDataGrid-root .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer': {
                pl: 1,
              },
              '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
            hideFooter={true}
            columnHeaderHeight={80}
          />
        </Stack>
      )}
      {!props.rateId && !props.chargeCategoryId && (
        <Stack>
          <Stack p={1} sx={{ backgroundColor: 'var(--AzureishWhite)' }}>
            <Typography variant="body">
              <b>{t('pms.booking.reservation_group_new.grid.missing_rate_or_charge_category')}</b>
            </Typography>
          </Stack>
        </Stack>
      )}
    </>
  )
}
